import React, { useEffect, useState } from "react";
import { CalendarCheck, Sparkles } from "lucide-react";
import { SALES_CLOSE_AT } from "@/lib/salesClosed";

function compute(now) {
  const diff = Math.max(0, SALES_CLOSE_AT - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: diff === 0 };
}

const pad = (n) => String(n).padStart(2, "0");

export default function TicketsCloseCountdown() {
  const [t, setT] = useState(() => compute(Date.now()));

  useEffect(() => {
    const id = setInterval(() => setT(compute(Date.now())), 1000);
    return () => clearInterval(id);
  }, []);

  if (t.done) {
    return (
      <div
        data-testid="tickets-close-countdown-done"
        className="relative overflow-hidden rounded-3xl border-2 border-[#FACC15]/60 bg-gradient-to-br from-[#0066FF]/30 via-[#0A1128] to-[#0A1128] p-8 md:p-10 text-center shadow-[0_0_60px_rgba(250,204,21,0.35)]"
      >
        <div className="text-[#FACC15] text-xs md:text-sm font-black uppercase tracking-[0.35em] mb-3">
          Ticket Sales Are Closed
        </div>
        <div className="font-display text-4xl md:text-6xl text-white leading-[0.9]">
          See you at the estate.
        </div>
        <p className="text-white/75 mt-4 max-w-lg mx-auto">
          Still interested? Message Jacob directly on Skool — spots occasionally open up.
        </p>
      </div>
    );
  }

  const cell = (n, label) => (
    <div
      data-testid={`tcc-${label.toLowerCase()}`}
      className="flex-1 min-w-0 bg-[#0A1128]/70 backdrop-blur-sm rounded-xl md:rounded-2xl border border-[#FACC15]/40 py-4 md:py-6 px-2 md:px-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
    >
      <div className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-[#FACC15] leading-none tabular-nums tracking-tight drop-shadow-[0_2px_10px_rgba(250,204,21,0.35)]">
        {label === "Days" ? n : pad(n)}
      </div>
      <div className="text-[10px] md:text-xs font-black uppercase tracking-[0.25em] text-white/75 mt-2 md:mt-3">
        {label}
      </div>
    </div>
  );

  return (
    <div
      data-testid="tickets-close-countdown"
      className="relative overflow-hidden rounded-3xl border-2 border-[#FACC15]/40 bg-gradient-to-br from-[#0066FF]/25 via-[#0A1128] to-[#0A1128] p-5 md:p-8 shadow-[0_0_60px_rgba(250,204,21,0.25)]"
    >
      {/* Glow rings */}
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#FACC15]/25 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-[#0066FF]/25 rounded-full blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5 md:mb-6">
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FACC15] opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FACC15]" />
            </span>
            <span className="text-[#FACC15] text-[10px] md:text-xs font-black uppercase tracking-[0.35em]">
              Last Chance · Sales Close Thursday
            </span>
          </div>
          <div className="inline-flex items-center gap-2 self-start md:self-auto text-[10px] md:text-xs font-black uppercase tracking-wider text-white/80">
            <Sparkles size={12} className="text-[#FACC15]" /> Palm Beach · Private Estate
          </div>
        </div>

        <div className="font-display text-2xl sm:text-3xl md:text-4xl text-white mb-4 md:mb-6 leading-tight">
          When this hits zero, <span className="text-[#FACC15]">doors close</span>. Grab a seat while you can.
        </div>

        <div className="flex gap-2 md:gap-4">
          {cell(t.d, "Days")}
          {cell(t.h, "Hours")}
          {cell(t.m, "Mins")}
          {cell(t.s, "Secs")}
        </div>

        <div className="mt-5 md:mt-6 flex items-center gap-2 text-xs md:text-sm text-white/75">
          <CalendarCheck size={14} className="text-[#FACC15]" />
          <span>Sales close: <span className="font-semibold text-white">Thursday, October 1 · 11:59 PM ET</span></span>
        </div>
      </div>
    </div>
  );
}
